// ABOUTME: Portfolio, position, transaction, and portfolio value-history API clients
// ABOUTME: Covers /portfolios/* endpoints and the value-history endpoint; payload types come from contracts

import type { ApiSuccessResponse } from '@alpha-finder/contracts/envelope';
import type {
  AdjustPositionRequest,
  CreatePortfolioRequest,
  CreatePositionRequest,
  CreateTransactionRequest,
  DeletedResult,
  EnrichedPortfolio,
  PaginatedPortfolios,
  Portfolio,
  PortfolioSummary,
  PortfolioWithPositions,
  Position,
  Transaction,
  TransactionDeleteResult,
  TransactionList,
  TransactionWriteResult,
  UpdatePortfolioRequest,
  UpdatePositionRequest,
  UpdateTransactionRequest,
  ValueHistoryPeriod,
  ValueHistoryResponse,
} from '@alpha-finder/contracts/portfolio';
import { apiClient } from './client';

export const portfolioManagementApi = {
  async list() {
    const response = await apiClient.get<ApiSuccessResponse<PaginatedPortfolios>>('/portfolios');
    return response.data.data;
  },

  async get(id: string) {
    const response = await apiClient.get<ApiSuccessResponse<PortfolioWithPositions>>(
      `/portfolios/${id}`
    );
    return response.data.data;
  },

  async getSummary(signal?: AbortSignal) {
    const response = await apiClient.get<ApiSuccessResponse<PortfolioSummary>>(
      '/portfolios/summary',
      {
        signal,
      }
    );
    return response.data.data;
  },

  async getEnriched(id: string) {
    const response = await apiClient.get<ApiSuccessResponse<EnrichedPortfolio>>(
      `/portfolios/${id}/enriched`
    );
    return response.data.data;
  },

  // Batch sibling of getEnriched — one request for many portfolios, collapsing
  // the list page's N per-portfolio round-trips into one.
  /** @deprecated Use getSummary for every portfolio or getEnriched for one. */
  async getEnrichedBatch(ids: string[]) {
    const response = await apiClient.get<ApiSuccessResponse<EnrichedPortfolio[]>>(
      `/portfolios/enriched?ids=${ids.map(encodeURIComponent).join(',')}`
    );
    return response.data.data;
  },

  async create(data: CreatePortfolioRequest) {
    const response = await apiClient.post<ApiSuccessResponse<Portfolio>>('/portfolios', data);
    return response.data.data;
  },

  async update(id: string, data: UpdatePortfolioRequest) {
    const response = await apiClient.put<ApiSuccessResponse<Portfolio>>(`/portfolios/${id}`, data);
    return response.data.data;
  },

  async delete(id: string) {
    const response = await apiClient.delete<ApiSuccessResponse<DeletedResult>>(`/portfolios/${id}`);
    return response.data.data;
  },

  async addPosition(portfolioId: string, data: CreatePositionRequest) {
    const response = await apiClient.post<ApiSuccessResponse<Position>>(
      `/portfolios/${portfolioId}/positions`,
      data
    );
    return response.data.data;
  },

  async updatePosition(portfolioId: string, positionId: string, data: UpdatePositionRequest) {
    const response = await apiClient.put<ApiSuccessResponse<Position>>(
      `/portfolios/${portfolioId}/positions/${positionId}`,
      data
    );
    return response.data.data;
  },

  async deletePosition(portfolioId: string, positionId: string) {
    const response = await apiClient.delete<ApiSuccessResponse<DeletedResult>>(
      `/portfolios/${portfolioId}/positions/${positionId}`
    );
    return response.data.data;
  },

  async adjustPosition(portfolioId: string, positionId: string, data: AdjustPositionRequest) {
    const response = await apiClient.post<ApiSuccessResponse<Position>>(
      `/portfolios/${portfolioId}/positions/${positionId}/adjust`,
      data
    );
    return response.data.data;
  },
};

export const transactionsApi = {
  async list(portfolioId: string, positionId: string): Promise<Transaction[]> {
    const response = await apiClient.get<ApiSuccessResponse<TransactionList>>(
      `/portfolios/${portfolioId}/positions/${positionId}/transactions`
    );
    return response.data.data.items;
  },

  async listByPortfolio(portfolioId: string): Promise<Transaction[]> {
    const response = await apiClient.get<ApiSuccessResponse<TransactionList>>(
      `/portfolios/${portfolioId}/transactions`
    );
    return response.data.data.items;
  },

  async create(portfolioId: string, positionId: string, data: CreateTransactionRequest) {
    const response = await apiClient.post<ApiSuccessResponse<TransactionWriteResult>>(
      `/portfolios/${portfolioId}/positions/${positionId}/transactions`,
      data
    );
    return response.data.data;
  },

  async update(
    portfolioId: string,
    positionId: string,
    txId: string,
    data: UpdateTransactionRequest
  ) {
    const response = await apiClient.patch<ApiSuccessResponse<TransactionWriteResult>>(
      `/portfolios/${portfolioId}/positions/${positionId}/transactions/${txId}`,
      data
    );
    return response.data.data;
  },

  async delete(portfolioId: string, positionId: string, txId: string) {
    const response = await apiClient.delete<ApiSuccessResponse<TransactionDeleteResult>>(
      `/portfolios/${portfolioId}/positions/${positionId}/transactions/${txId}`
    );
    return response.data.data;
  },
};

// Portfolio value history (invested vs market value over time)
export const portfolioHistoryApi = {
  async getValueHistory(id: string, period: ValueHistoryPeriod): Promise<ValueHistoryResponse> {
    const response = await apiClient.get<ApiSuccessResponse<ValueHistoryResponse>>(
      `/portfolios/${id}/value-history?period=${period}`
    );
    return response.data.data;
  },
};
